/**
 * As chamadas do pré-credenciado (F09). Contrato em
 * docs/credenciamento-ponta-a-ponta/frentes/F09.md, seção 2.4.
 *
 * - Resposta do inscrito: sessão do inscrito (`publica` + `credenciais`, o
 *   cookie `HttpOnly` de F04). A API deriva o dono da sessão.
 * - Link do e-mail: rota pública, sem sessão — o token é a credencial.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { chamarApi, ErroApi } from '@/nucleo/auth/cliente-api';

const BASE = '/bff/v1/pre-credenciamento';
const COM_SESSAO = { publica: true, credenciais: true } as const;

export interface SituacaoPreCredenciamento {
  protocolo: string;
  placa: string;
  status: string;
  prazoRespostaEm: string;
  vencido: boolean;
  resposta: 'CONFIRMADO' | 'DESISTENTE' | null;
  respondidoEm: string | null;
  podeConfirmar: boolean;
  podeDesistir: boolean;
  acesso: 'PREPARANDO' | 'EMAIL_ENVIADO' | 'LIBERADO' | 'PENDENTE_DTP';
}

export type ResultadoDoLink = 'ENVIADO' | 'SENHA_JA_DEFINIDA' | 'LINK_INVALIDO' | 'INDISPONIVEL';

export const chaveDaSituacao = (protocolo: string) =>
  ['portal', 'pre-credenciamento', protocolo] as const;

function repetirSoFalhaTecnica(tentativas: number, erro: ErroApi): boolean {
  return tentativas < 1 && !(erro instanceof ErroApi && erro.situacao < 500);
}

export function useSituacaoPreCredenciamento(
  protocolo: string,
): UseQueryResult<SituacaoPreCredenciamento, ErroApi> {
  return useQuery<SituacaoPreCredenciamento, ErroApi>({
    queryKey: chaveDaSituacao(protocolo),
    queryFn: ({ signal }) =>
      chamarApi(`${BASE}/veiculos/${encodeURIComponent(protocolo)}`, {
        ...COM_SESSAO,
        sinal: signal,
      }),
    retry: repetirSoFalhaTecnica,
  });
}

/** Confirmar ou desistir. A situação que volta substitui a do cache. */
export function useResponderPreCredenciamento(protocolo: string) {
  const cliente = useQueryClient();
  return useMutation<
    SituacaoPreCredenciamento,
    ErroApi,
    { acao: 'confirmar' | 'desistir'; chaveIdempotencia: string; motivo?: string }
  >({
    mutationFn: ({ acao, ...corpo }) =>
      chamarApi(`${BASE}/veiculos/${encodeURIComponent(protocolo)}/${acao}`, {
        ...COM_SESSAO,
        metodo: 'POST',
        corpo,
      }),
    onSuccess: (situacao) => {
      cliente.setQueryData(chaveDaSituacao(protocolo), situacao);
    },
  });
}

export function useDefinirSenha() {
  return useMutation<{ resultado: ResultadoDoLink }, ErroApi, string>({
    mutationFn: (token) =>
      chamarApi(`${BASE}/acesso/definir-senha`, {
        publica: true,
        metodo: 'POST',
        corpo: { token },
      }),
  });
}
